// pages/RegisterPage.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { 
  FiGrid, 
  FiUser, 
  FiMail, 
  FiLock, 
  FiArrowRight, 
  FiEye, 
  FiEyeOff,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import './AuthPages.css';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const { register } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Memoized validation functions to prevent re-creation on each render
  const validateField = useCallback((field, value) => {
    const errors = {};
    
    switch(field) {
      case 'name':
        if (!value || value.trim() === '') {
          errors.name = 'Full name is required';
        } else if (value.length < 2) {
          errors.name = 'Name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s\-']+$/.test(value)) {
          errors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
      
      case 'email':
        if (!value || value.trim() === '') {
          errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Please enter a valid email address';
        }
        break;
      
      case 'password':
        if (!value) {
          errors.password = 'Password is required';
        } else if (value.length < 8) {
          errors.password = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          errors.password = 'Password must contain uppercase, lowercase, and number';
        }
        break;
      
      case 'confirmPassword':
        if (value && value !== formData.password) {
          errors.confirmPassword = 'Passwords do not match';
        }
        break;
      
      default:
        break;
    }
    
    return errors;
  }, [formData.password]); // Add formData.password as dependency

  // Calculate password strength using useMemo to avoid recalculating unnecessarily
  const passwordStrength = useMemo(() => {
    const value = formData.password;
    if (!value) return 0;
    
    let strength = 0;
    if (value.length >= 8) strength++;
    if (/(?=.*[a-z])/.test(value)) strength++;
    if (/(?=.*[A-Z])/.test(value)) strength++;
    if (/(?=.*\d)/.test(value)) strength++;
    if (/(?=.*[!@#$%^&*])/.test(value)) strength++;
    return strength;
  }, [formData.password]);

  const getFieldError = useCallback((field) => {
    const errors = validateField(field, formData[field]);
    return errors[field] || null;
  }, [validateField, formData]);

  const isFieldValid = useCallback((field) => {
    if (!touched[field]) return true;
    return !getFieldError(field);
  }, [touched, getFieldError]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);

  const handleBlur = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const getPasswordStrengthColor = useCallback(() => {
    const colors = ['#e2e8f0', '#f56565', '#ed8936', '#ecc94b', '#48bb78'];
    return colors[passwordStrength] || colors[0];
  }, [passwordStrength]);

  const getPasswordStrengthText = useCallback(() => {
    if (passwordStrength <= 1) return 'Weak';
    if (passwordStrength <= 2) return 'Fair';
    if (passwordStrength <= 3) return 'Good';
    return 'Strong';
  }, [passwordStrength]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const errors = {};
    Object.keys(formData).forEach(field => {
      const fieldErrors = validateField(field, formData[field]);
      Object.assign(errors, fieldErrors);
    });
    
    if (Object.keys(errors).length > 0) {
      // Show first error
      const firstError = Object.values(errors)[0];
      showToast(firstError, 'error');
      return;
    }

    setSubmitting(true);
    try {
      const { name, email, password, confirmPassword } = formData;
      await register(name, email, password, confirmPassword);
      showToast('Account created successfully! Let\'s setup your business.', 'success');
      navigate('/setup-business');
    } catch (err) {
      showToast(err.message || 'Registration failed. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <img src='/assets/logbg.png' />
        <div className="auth-card">
          <div className="auth-header">
            <img src='/assets/metlogo.png' />
            <h1 className="auth-title">Get Started Free</h1>
            <p className="auth-subtitle">Create your digital business page in minutes</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="name">
                Full Name
              </label>
              <div className={`input-wrapper ${touched.name && !isFieldValid('name') ? 'error' : ''}`}>
                <FiUser className="input-icon" />
                <input
                  id="name"
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('name')}
                  disabled={submitting}
                  autoComplete="name"
                />
                {touched.name && !isFieldValid('name') && (
                  <FiAlertCircle className="input-error-icon" />
                )}
                {touched.name && isFieldValid('name') && formData.name && (
                  <FiCheckCircle className="input-success-icon" />
                )}
              </div>
              {touched.name && getFieldError('name') && (
                <span className="error-message">{getFieldError('name')}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="email">
                Email Address
              </label>
              <div className={`input-wrapper ${touched.email && !isFieldValid('email') ? 'error' : ''}`}>
                <FiMail className="input-icon" />
                <input
                  id="email"
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur('email')}
                  disabled={submitting}
                  autoComplete="email"
                />
                {touched.email && !isFieldValid('email') && (
                  <FiAlertCircle className="input-error-icon" />
                )}
                {touched.email && isFieldValid('email') && formData.email && (
                  <FiCheckCircle className="input-success-icon" />
                )}
              </div>
              {touched.email && getFieldError('email') && (
                <span className="error-message">{getFieldError('email')}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <div className={`input-wrapper ${touched.password && !isFieldValid('password') ? 'error' : ''}`}>
                <FiLock className="input-icon" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                  disabled={submitting}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(prev => !prev)}
                  tabIndex="-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
                {touched.password && !isFieldValid('password') && (
                  <FiAlertCircle className="input-error-icon" />
                )}
                {touched.password && isFieldValid('password') && formData.password && (
                  <FiCheckCircle className="input-success-icon" />
                )}
              </div>
              {touched.password && getFieldError('password') && (
                <span className="error-message">{getFieldError('password')}</span>
              )}
              
              {touched.password && formData.password && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div 
                      className="strength-indicator"
                      style={{ 
                        width: `${(passwordStrength / 5) * 100}%`,
                        backgroundColor: getPasswordStrengthColor()
                      }}
                    />
                  </div>
                  <span className="strength-text">
                    Password Strength: {getPasswordStrengthText()}
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirmPassword">
                Confirm Password
              </label>
              <div className={`input-wrapper ${touched.confirmPassword && !isFieldValid('confirmPassword') ? 'error' : ''}`}>
                <FiLock className="input-icon" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  className="form-input"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={() => handleBlur('confirmPassword')}
                  disabled={submitting}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  tabIndex="-1"
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
                {touched.confirmPassword && !isFieldValid('confirmPassword') && (
                  <FiAlertCircle className="input-error-icon" />
                )}
                {touched.confirmPassword && isFieldValid('confirmPassword') && formData.confirmPassword && (
                  <FiCheckCircle className="input-success-icon" />
                )}
              </div>
              {touched.confirmPassword && getFieldError('confirmPassword') && (
                <span className="error-message">{getFieldError('confirmPassword')}</span>
              )}
            </div>

            <button 
              type="submit" 
              className={`btn-primary ${submitting ? 'loading' : ''}`}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner"></span>
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account <FiArrowRight />
                </>
              )}
            </button>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;